import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Badge,
  ActionIcon,
  TextInput,
  Button,
  Text,
  Divider
} from '@mantine/core';
import { IconX, IconPlus, IconWorldWww } from '@tabler/icons-react';
import { useUpdateSubredditsMutation } from '../../../../application/queries/regionQueries';

/**
 * Props for ManageSubredditsModal component
 */
export interface ManageSubredditsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Region ID to update */
  regionId: string;
  /** Current subreddits for the region */
  currentSubreddits: string[];
  /** Callback when subreddits are successfully updated */
  onSuccess: () => void;
}

/**
 * ManageSubredditsModal - Modal for adding/removing subreddits from a region
 *
 * Features:
 * - Display current subreddits as removable badges
 * - Add new subreddits via text input
 * - Validation (no duplicates, no empty strings, strip "r/" prefix)
 * - Confirmation on remove
 * - Success/error toast notifications via React Query
 */
export const ManageSubredditsModal: React.FC<ManageSubredditsModalProps> = ({
  isOpen,
  onClose,
  regionId,
  currentSubreddits,
  onSuccess
}): React.ReactElement => {
  // Local state for subreddit list (working copy)
  const [subreddits, setSubreddits] = useState<string[]>([]);
  const [newSubreddit, setNewSubreddit] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // React Query mutation
  const updateSubredditsMutation = useUpdateSubredditsMutation();

  /**
   * Initialize local state when modal opens or currentSubreddits changes
   */
  useEffect(() => {
    if (isOpen) {
      setSubreddits([...currentSubreddits]);
      setNewSubreddit('');
      setValidationError(null);
    }
  }, [isOpen, currentSubreddits]);

  /**
   * Normalize subreddit name: strip "r/" prefix, trim whitespace, lowercase for comparison
   */
  const normalizeSubredditName = useCallback((name: string): string => {
    let normalized = name.trim();
    if (normalized.toLowerCase().startsWith('r/')) {
      normalized = normalized.substring(2);
    }
    return normalized.trim();
  }, []);

  /**
   * Validate new subreddit name
   */
  const validateSubreddit = useCallback(
    (name: string): string | null => {
      const normalized = normalizeSubredditName(name);

      if (normalized.length === 0) {
        return 'Subreddit name cannot be empty';
      }

      // Check for duplicates (case-insensitive)
      const isDuplicate = subreddits.some(
        (existing) => existing.toLowerCase() === normalized.toLowerCase()
      );

      if (isDuplicate) {
        return `Subreddit "${normalized}" is already added`;
      }

      return null;
    },
    [subreddits, normalizeSubredditName]
  );

  /**
   * Add new subreddit to the list
   */
  const handleAddSubreddit = useCallback((): void => {
    const error = validateSubreddit(newSubreddit);

    if (error) {
      setValidationError(error);
      return;
    }

    const normalized = normalizeSubredditName(newSubreddit);
    setSubreddits((prev) => [...prev, normalized]);
    setNewSubreddit('');
    setValidationError(null);
  }, [newSubreddit, validateSubreddit, normalizeSubredditName]);

  /**
   * Remove subreddit with confirmation
   */
  const handleRemoveSubreddit = useCallback((subredditToRemove: string): void => {
    // Use native browser confirmation
    const confirmed = window.confirm(
      `Are you sure you want to remove r/${subredditToRemove} from this region?`
    );

    if (confirmed) {
      setSubreddits((prev) => prev.filter((s) => s !== subredditToRemove));
    }
  }, []);

  /**
   * Save changes to backend
   */
  const handleSave = useCallback(async (): Promise<void> => {
    try {
      await updateSubredditsMutation.mutateAsync({
        regionId,
        subreddits
      });

      // Success callback
      onSuccess();
      onClose();
    } catch (error) {
      // Error handled by mutation hook (toast notification)
      console.error('Failed to update subreddits:', error);
    }
  }, [regionId, subreddits, updateSubredditsMutation, onSuccess, onClose]);

  /**
   * Handle Enter key in text input
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddSubreddit();
      }
    },
    [handleAddSubreddit]
  );

  /**
   * Check if there are unsaved changes
   */
  const hasChanges =
    JSON.stringify([...subreddits].sort()) !== JSON.stringify([...currentSubreddits].sort());

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Manage Subreddits"
      size="lg"
      closeOnClickOutside={!updateSubredditsMutation.isPending}
      closeOnEscape={!updateSubredditsMutation.isPending}
    >
      <Stack gap="md">
        {/* Instructions */}
        <Text size="sm" c="dimmed">
          Add or remove subreddits to monitor for mentions in this region. Enter subreddit names
          without the "r/" prefix (e.g., "Seattle" not "r/Seattle").
        </Text>

        <Divider />

        {/* Add Subreddit Input */}
        <Group gap="sm" align="flex-start">
          <TextInput
            placeholder="Enter subreddit name (e.g., Seattle)"
            value={newSubreddit}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNewSubreddit(event.currentTarget.value);
              setValidationError(null);
            }}
            onKeyDown={handleKeyDown}
            error={validationError}
            style={{ flex: 1 }}
            disabled={updateSubredditsMutation.isPending}
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddSubreddit}
            disabled={updateSubredditsMutation.isPending}
          >
            Add
          </Button>
        </Group>

        <Divider />

        {/* Current Subreddits */}
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Current Subreddits ({subreddits.length})
          </Text>

          {subreddits.length > 0 ? (
            <Group gap="xs">
              {subreddits.map((subreddit) => (
                <Badge
                  key={subreddit}
                  size="lg"
                  variant="outline"
                  leftSection={<IconWorldWww size={14} />}
                  rightSection={
                    <ActionIcon
                      size="xs"
                      color="red"
                      variant="subtle"
                      onClick={() => handleRemoveSubreddit(subreddit)}
                      disabled={updateSubredditsMutation.isPending}
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  }
                  pr={3}
                >
                  r/{subreddit}
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No subreddits configured. Add at least one subreddit to monitor mentions.
            </Text>
          )}
        </Stack>

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            onClick={onClose}
            disabled={updateSubredditsMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={updateSubredditsMutation.isPending}
            disabled={!hasChanges || updateSubredditsMutation.isPending}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
